import './global-types.js';
import SchemaBuilder, { brandWithType, completeValue, PothosError } from '@pothos/core';
import { PrismaInterfaceRef } from './interface-ref.js';
import { ModelLoader } from './model-loader.js';
import { PrismaNodeRef } from './node-ref.js';
import { PrismaObjectRef } from './object-ref.js';
import { PrismaObjectFieldBuilder } from './prisma-field-builder.js';
import { getDefaultIDParser, getDefaultIDSerializer } from './util/cursors.js';
import { getDelegateFromModel, getRefFromModel } from './util/datamodel.js';
import { getModelDescription } from './util/description.js';
import { getClient, getDMMF } from './util/get-client.js';
import { queryFromInfo } from './util/map-query.js';
import { getRelationMap } from './util/relation-map.js';
const schemaBuilderProto = SchemaBuilder.prototype;
schemaBuilderProto.prismaObject = function prismaObject(type, { fields, findUnique, select, include, description, ...options }) {
    const ref = options.variant ? new PrismaObjectRef(options.variant, type) : getRefFromModel(type, this);
    var _options_variant, _ref;
    const name = (_ref = (_options_variant = options.variant) !== null && _options_variant !== void 0 ? _options_variant : options.name) !== null && _ref !== void 0 ? _ref : type;
    const fieldMap = getRelationMap(getDMMF(this)).get(type);
    const idSelection = ModelLoader.getDefaultIDSelection(ref, type, this);
    ref.name = name;
    this.objectType(ref, {
        ...options,
        description: getModelDescription(type, this, description),
        extensions: {
            ...options.extensions,
            pothosPrismaInclude: include,
            pothosPrismaModel: type,
            pothosPrismaFieldMap: fieldMap,
            pothosPrismaSelect: select && {
                ...idSelection,
                ...select
            },
            pothosPrismaLoader: ModelLoader.forRef(ref, type, findUnique, this)
        },
        name,
        fields: fields ? () => fields(new PrismaObjectFieldBuilder(name, this, type, getRelationMap(getDMMF(this)).get(type))) : undefined
    });
    return ref;
};
schemaBuilderProto.prismaInterface = function prismaInterface(type, { fields, findUnique, select, include, description, ...options }) {
    const ref = options.variant ? new PrismaInterfaceRef(options.variant, type) : getRefFromModel(type, this, "interface");
    var _options_variant, _ref;
    const name = (_ref = (_options_variant = options.variant) !== null && _options_variant !== void 0 ? _options_variant : options.name) !== null && _ref !== void 0 ? _ref : type;
    const fieldMap = getRelationMap(getDMMF(this)).get(type);
    const idSelection = ModelLoader.getDefaultIDSelection(ref, type, this);
    ref.name = name;
    this.interfaceType(ref, {
        ...options,
        description: getModelDescription(type, this, description),
        extensions: {
            ...options.extensions,
            pothosPrismaInclude: include,
            pothosPrismaModel: type,
            pothosPrismaFieldMap: fieldMap,
            pothosPrismaSelect: select && {
                ...idSelection,
                ...select
            },
            pothosPrismaLoader: ModelLoader.forRef(ref, type, findUnique, this)
        },
        name,
        fields: fields ? () => fields(new PrismaObjectFieldBuilder(name, this, type, getRelationMap(getDMMF(this)).get(type), "Interface")) : undefined
    });
    return ref;
};
schemaBuilderProto.prismaNode = function prismaNode(type, { id: { field, resolve: rawResolve, ...idOptions }, findUnique: rawFindUnique, name, variant, nullable, ...options }) {
    var _this_nodeInterfaceRef, _this;
    const fieldName = field;
    const interfaceRef = (_this_nodeInterfaceRef = (_this = this).nodeInterfaceRef) === null || _this_nodeInterfaceRef === void 0 ? void 0 : _this_nodeInterfaceRef.call(_this);
    const resolve = rawResolve !== null && rawResolve !== void 0 ? rawResolve : getDefaultIDSerializer(type, fieldName, this);
    const idParser = fieldName ? getDefaultIDParser(type, fieldName, this) : undefined;
    var _ref;
    const typeName = (_ref = variant !== null && variant !== void 0 ? variant : name) !== null && _ref !== void 0 ? _ref : type;
    const nodeRef = new PrismaNodeRef(typeName, type);
    const findUnique = rawFindUnique ? (parent, context) => rawFindUnique(resolve(parent, context), context) : ModelLoader.getFindUniqueForField(nodeRef, type, fieldName, this);
    if (!interfaceRef) {
        throw new PothosError("builder.prismaNode requires @pothos/plugin-relay to be installed");
    }
    const extendedOptions = {
        ...options,
        name,
        variant,
        interfaces: [
            interfaceRef
        ],
        findUnique,
        loadWithoutCache: async (id, context, info) => {
            const query = queryFromInfo({
                context,
                info,
                typeName
            });
            const delegate = getDelegateFromModel(getClient(this, context), type);
            const record = await (delegate.findUniqueOrThrow && !nullable ? delegate.findUniqueOrThrow({
                ...query,
                where: rawFindUnique ? rawFindUnique(id, context) : {
                    [fieldName]: idParser(id)
                }
            }) : delegate.findUnique({
                ...query,
                ...nullable ? {} : {
                    rejectOnNotFound: true
                },
                where: rawFindUnique ? rawFindUnique(id, context) : {
                    [fieldName]: idParser(id)
                }
            }));
            brandWithType(record, typeName);
            return record;
        }
    };
    const ref = this.prismaObject(type, extendedOptions);
    if (options.interfaces) {
        this.configStore.addInterfaces(typeName, options.interfaces);
    }
    this.configStore.onTypeConfig(ref, (nodeConfig) => {
        var _this_options_relayOptions;
        var _this_options_relayOptions_idFieldName;
        this.objectField(ref, (_this_options_relayOptions_idFieldName = (_this_options_relayOptions = this.options.relayOptions) === null || _this_options_relayOptions === void 0 ? void 0 : _this_options_relayOptions.idFieldName) !== null && _this_options_relayOptions_idFieldName !== void 0 ? _this_options_relayOptions_idFieldName : "id", (t) => {
            var _this_options_relayOptions;
            return t.globalID({
                ...(_this_options_relayOptions = this.options.relayOptions) === null || _this_options_relayOptions === void 0 ? void 0 : _this_options_relayOptions.idFieldOptions,
                ...idOptions,
                nullable: false,
                args: {},
                resolve: (parent, args, context, info) => completeValue(resolve(parent, context), (id) => ({
                    type: nodeConfig.name,
                    id
                }))
            });
        });
    });
    this.configStore.associateRefWithName(nodeRef, typeName);
    return nodeRef;
};
schemaBuilderProto.prismaObjectField = function prismaObjectField(type, fieldName, field) {
    const ref = typeof type === "string" ? getRefFromModel(type, this) : type;
    this.configStore.onTypeConfig(ref, ({ name }) => {
        this.configStore.addFields(ref, () => ({
            [fieldName]: field(new PrismaObjectFieldBuilder(name, this, ref.modelName, getRelationMap(getDMMF(this)).get(ref.modelName)))
        }));
    });
};
schemaBuilderProto.prismaInterfaceField = function prismaInterfaceField(type, fieldName, field) {
    const ref = typeof type === "string" ? getRefFromModel(type, this) : type;
    this.configStore.onTypeConfig(ref, ({ name }) => {
        this.configStore.addFields(ref, () => ({
            [fieldName]: field(new PrismaObjectFieldBuilder(name, this, ref.modelName, getRelationMap(getDMMF(this)).get(ref.modelName), "Interface"))
        }));
    });
};
schemaBuilderProto.prismaObjectFields = function prismaObjectFields(type, fields) {
    const ref = typeof type === "string" ? getRefFromModel(type, this) : type;
    this.configStore.onTypeConfig(ref, ({ name }) => {
        this.configStore.addFields(ref, () => fields(new PrismaObjectFieldBuilder(name, this, ref.modelName, getRelationMap(getDMMF(this)).get(ref.modelName))));
    });
};
schemaBuilderProto.prismaInterfaceFields = function prismaInterfaceFields(type, fields) {
    const ref = typeof type === "string" ? getRefFromModel(type, this) : type;
    this.configStore.onTypeConfig(ref, ({ name }) => {
        this.configStore.addFields(ref, () => fields(new PrismaObjectFieldBuilder(name, this, ref.modelName, getRelationMap(getDMMF(this)).get(ref.modelName), "Interface")));
    });
};
//# sourceMappingURL=schema-builder.js.map
